// src/hooks/useTribeGamification.ts
// Hook to fetch and manage tribe (profile group) gamification data
// Provides analytics, scores, and tier information

import { useQuery, useMutation, useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Id } from '@/convex/_generated/dataModel';

interface TribeAnalytics {
	tribeId: Id<'profileGroups'>;
	totalFollowers: number;
	totalEngagement: number;
	totalViews: number;
	memberCount: number;
	activeMemberCount: number;
	score: number;
	tier?: string;
	followersGrowthRate?: number;
	engagementGrowthRate?: number;
	artworkUrl?: string;
	communityShareBonus?: number;
	shareCount?: number;
	lastUpdated: string;
}

interface UseTribeGamificationProps {
	tribeId?: Id<'profileGroups'>;
	enabled?: boolean;
}

export function useTribeGamification({ 
	tribeId, 
	enabled = true 
}: UseTribeGamificationProps) {
	// Fetch tribe analytics
	const analytics = useQuery(
		api.tribeAnalytics.getTribeAnalytics,
		enabled && tribeId ? { tribeId } : 'skip'
	) as TribeAnalytics | undefined;

	// Initialize gamification for tribe
	const initializeGamification = useMutation(
		api.tribeAnalytics.initializeTribeAnalytics
	);

	// Generate tribe artwork
	const generateArtwork = useAction(
		api.tribeAnalytics.generateTribeArtwork
	);

	// Track community share
	const trackShare = useMutation(
		api.tribeAnalytics.trackCommunityShare
	);

	// Determine tier color scheme
	const tierConfig = {
		common: {
			color: 'from-gray-600 to-gray-400',
			borderColor: 'border-gray-500',
			icon: '⚪',
			label: 'Common',
		},
		uncommon: {
			color: 'from-green-600 to-green-400',
			borderColor: 'border-green-500',
			icon: '🟢',
			label: 'Uncommon',
		},
		rare: {
			color: 'from-blue-600 to-blue-400',
			borderColor: 'border-blue-500',
			icon: '🔵',
			label: 'Rare',
		},
		epic: {
			color: 'from-purple-600 to-purple-400',
			borderColor: 'border-purple-500',
			icon: '🟣',
			label: 'Epic',
		},
		legendary: {
			color: 'from-orange-600 to-orange-400',
			borderColor: 'border-orange-500',
			icon: '🟠',
			label: 'Legendary',
		},
	};

	const currentTierConfig = analytics?.tier 
		? tierConfig[analytics.tier as keyof typeof tierConfig] || tierConfig.common
		: tierConfig.common;

	return {
		analytics,
		isLoading: analytics === undefined && enabled && !!tribeId,
		initializeGamification,
		generateArtwork,
		trackShare,
		tierConfig: currentTierConfig,
	};
}